import { apiClient } from '@borradh-workspace/api-client';
import type {
  ApplyAnalysisResponse,
  ApplySnapshotInput,
} from '@borradh-workspace/api-client/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { invalidateKeys, queryKeys } from '@/lib/query-keys';

interface UseApplySnapshotOptions {
  onSuccess?: (result: ApplyAnalysisResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Onboarding-only writer: hands the wizard's final snapshot to
 * `applyWebsiteAnalysis` via `POST /website-analysis/apply-snapshot`. The
 * caller MUST have already created the org and set it active on the session,
 * because this endpoint reads `organizationId` from `@ActiveOrganization`.
 *
 * The wizard used to run three sequential item loops (createService,
 * createLocation, createPractitioner) that silently dropped packages,
 * variants and memberships from whatever the scanner or operator upload had
 * produced. Routing through the writer is what actually lands those.
 *
 * No toast here — the wizard renders its own end-of-flow summary based on
 * the returned counters.
 *
 * It does invalidate, though, the same roots `useApplyAnalysis` does. The
 * onboarding layout reads the locations list as soon as the org exists, which
 * is WHILE this write is in flight, so it cached an empty list for five
 * minutes: the wizard finished into a dashboard whose sidebar said "Add a
 * location" for a business that had one.
 */
export const useApplySnapshot = (options?: UseApplySnapshotOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: ApplySnapshotInput) =>
      // Longer than the wizard's own 60s wait. The api-client's 30s default
      // failed a slow write before the wizard could say "taking longer", and
      // the failure dropped the in-flight request it is meant to wait on, so
      // Continue raced a second write against one still running server-side.
      apiClient.post<ApplyAnalysisResponse>(
        'website-analysis/apply-snapshot',
        input,
        { timeout: 120_000 }
      ),
    onSuccess: (result) => {
      invalidateKeys(
        queryClient,
        queryKeys.organizationServices.all(),
        queryKeys.organizationLocations.all(),
        queryKeys.locationOpeningHours.all(),
        queryKeys.practitioners.all(),
        queryKeys.venue.all()
      );
      options?.onSuccess?.(result);
    },
    onError: (error: Error) => options?.onError?.(error),
  });

  return {
    applySnapshot: mutation.mutate,
    applySnapshotAsync: mutation.mutateAsync,
    isApplying: mutation.isPending,
  };
};
